/**
 * Vehicle risk view model and helpers (Milestone 10).
 */
import { AVAILABILITY_STATUS } from "../constants/automapListingConstants";
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";
import {
  RISK_DETAIL_COPY_ES,
  RISK_DETAIL_TITLES_ES,
  RISK_DISCLAIMER_ES,
  RISK_FLAG_DESCRIPTIONS_ES,
  RISK_FLAG_LABELS_ES,
  RISK_FLAG_QUESTIONS_ES,
  RISK_FLAGS,
  RISK_LEVEL_CLASS,
  RISK_LEVEL_LABELS_ES,
  RISK_LEVEL_SHORT_LABELS_ES,
  RISK_LEVEL_TOOLTIPS_ES,
  RISK_LEVELS,
  RECOMMENDATION_RISK_ADJUSTMENTS,
} from "../constants/automapVehicleRiskConstants";

const STALE_DAYS = 14;
const HIGH_PRIORITY_FLAGS = new Set([
  RISK_FLAGS.TRUST_SAFETY_REPORT_OPEN,
  RISK_FLAGS.DUPLICATE_CANDIDATE,
  RISK_FLAGS.DEALER_CONTACT_MISSING,
]);

const MEDIUM_PRIORITY_FLAGS = new Set([
  RISK_FLAGS.STALE_LISTING,
  RISK_FLAGS.AVAILABILITY_NOT_CONFIRMED,
  RISK_FLAGS.MISSING_MILEAGE,
  RISK_FLAGS.MISSING_PRICE,
  RISK_FLAGS.MISSING_PHOTOS,
  RISK_FLAGS.FEW_PHOTOS,
  RISK_FLAGS.PRICE_OUTLIER_HIGH,
  RISK_FLAGS.PRICE_OUTLIER_LOW,
  RISK_FLAGS.HIGH_MILEAGE_FOR_YEAR,
  RISK_FLAGS.DEALER_UNVERIFIED,
  RISK_FLAGS.LISTING_QUALITY_LOW,
]);

const VALID_LEVELS = new Set(Object.values(RISK_LEVELS));

function normalizeLevel(level) {
  const key = String(level || "").toLowerCase();
  return VALID_LEVELS.has(key) ? key : RISK_LEVELS.UNKNOWN;
}

function normalizeRiskFlag(flag) {
  if (!flag) return null;
  if (typeof flag === "string") return flag;
  if (flag.type) return String(flag.type);
  if (flag.key) return String(flag.key);
  return null;
}

function uniqueFlags(flags) {
  return [...new Set(flags.filter(Boolean))];
}

function daysSince(isoDate) {
  if (!isoDate) return null;
  const ts = Date.parse(isoDate);
  if (Number.isNaN(ts)) return null;
  return (Date.now() - ts) / (1000 * 60 * 60 * 24);
}

function hasRealPhotos(listing) {
  const count = listing?.imageCount ?? listing?.images?.length ?? 0;
  return count > 0;
}

export function deriveRiskFlagsFromListing(listing) {
  if (!listing) {
    return [RISK_FLAGS.NO_RISK_DATA];
  }

  const backend = uniqueFlags((listing.riskFlags || []).map(normalizeRiskFlag));
  if (backend.length) return backend;

  if (!listing.id && !listing.make && !listing.model) {
    return [RISK_FLAGS.NO_RISK_DATA];
  }

  const derived = [];

  const staleDays = daysSince(listing.lastConfirmedAt || listing.updatedAt);
  if (staleDays == null || staleDays > STALE_DAYS) {
    derived.push(RISK_FLAGS.STALE_LISTING);
  }

  if (
    listing.availabilityStatus === AVAILABILITY_STATUS.PENDING_CONFIRMATION ||
    listing.availabilityStatus === AVAILABILITY_STATUS.UNKNOWN
  ) {
    derived.push(RISK_FLAGS.AVAILABILITY_NOT_CONFIRMED);
  }

  if (listing.mileageKm == null) derived.push(RISK_FLAGS.MISSING_MILEAGE);
  if (listing.priceCop == null) derived.push(RISK_FLAGS.MISSING_PRICE);

  const imageCount = listing.imageCount ?? listing.images?.length ?? 0;
  if (imageCount === 0) derived.push(RISK_FLAGS.MISSING_PHOTOS);
  else if (imageCount < 3) derived.push(RISK_FLAGS.FEW_PHOTOS);

  if (!listing.dealerVerified) derived.push(RISK_FLAGS.DEALER_UNVERIFIED);
  if (!listing.dealerWhatsapp && !listing.dealerPhone) {
    derived.push(RISK_FLAGS.DEALER_CONTACT_MISSING);
  }
  if (!listing.city && !listing.locationLabel) derived.push(RISK_FLAGS.LOCATION_MISSING);

  if (listing.fairPriceLabel === FAIR_PRICE_LABELS.high_price) {
    derived.push(RISK_FLAGS.PRICE_OUTLIER_HIGH);
  }
  if (
    listing.fairPriceLabel === FAIR_PRICE_LABELS.below_market &&
    listing.fairPriceDeltaCop != null &&
    Number(listing.fairPriceDeltaCop) < -8_000_000
  ) {
    derived.push(RISK_FLAGS.PRICE_OUTLIER_LOW);
  }

  if (listing.year && listing.mileageKm != null) {
    const age = Math.max(1, new Date().getFullYear() - Number(listing.year));
    const kmPerYear = Number(listing.mileageKm) / age;
    if (kmPerYear > 25_000) derived.push(RISK_FLAGS.HIGH_MILEAGE_FOR_YEAR);
  }

  if (listing.qualityScore != null && Number(listing.qualityScore) < 45) {
    derived.push(RISK_FLAGS.LISTING_QUALITY_LOW);
  }
  if (listing.hasIncompleteInfo) derived.push(RISK_FLAGS.IMPORTED_DATA_INCOMPLETE);

  if (listing.trustSafetyCaseCount > 0 || listing.trustSafetyReportOpen) {
    derived.push(RISK_FLAGS.TRUST_SAFETY_REPORT_OPEN);
  }

  if (listing.duplicateCandidate) derived.push(RISK_FLAGS.DUPLICATE_CANDIDATE);

  return uniqueFlags(derived);
}

export function deriveRiskLevelFromFlags(flags = [], listing = null) {
  const normalized = uniqueFlags(flags.map(normalizeRiskFlag));
  if (!normalized.length || (normalized.length === 1 && normalized[0] === RISK_FLAGS.NO_RISK_DATA)) {
    return RISK_LEVELS.UNKNOWN;
  }

  if (normalized.some((flag) => HIGH_PRIORITY_FLAGS.has(flag))) {
    return RISK_LEVELS.HIGH;
  }

  const mediumCount = normalized.filter((flag) => MEDIUM_PRIORITY_FLAGS.has(flag)).length;
  if (mediumCount >= 3) return RISK_LEVELS.HIGH;
  if (mediumCount >= 1) return RISK_LEVELS.MEDIUM;

  if (normalized.length > 0) return RISK_LEVELS.LOW;

  if (listing?.riskLevel) return normalizeLevel(listing.riskLevel);
  return RISK_LEVELS.UNKNOWN;
}

export function resolveRiskLevel(listing) {
  if (!listing) return RISK_LEVELS.UNKNOWN;
  const explicit = listing.riskLevel || listing.vehicleRiskLevel;
  if (explicit && normalizeLevel(explicit) !== RISK_LEVELS.UNKNOWN) {
    return normalizeLevel(explicit);
  }
  const flags = deriveRiskFlagsFromListing(listing);
  return deriveRiskLevelFromFlags(flags, listing);
}

export function getRiskLevelLabel(level, { short = false, t = null } = {}) {
  const key = normalizeLevel(level);
  if (t) {
    const catalogKey = short
      ? `automotive.badges.riskShort.${key}`
      : `automotive.badges.risk.${key}`;
    return resolveCatalogLabel(t, catalogKey, RISK_LEVEL_LABELS_ES[key] || RISK_LEVEL_LABELS_ES.unknown);
  }
  const map = short ? RISK_LEVEL_SHORT_LABELS_ES : RISK_LEVEL_LABELS_ES;
  return map[key] || RISK_LEVEL_LABELS_ES.unknown;
}

function resolveCatalogLabel(t, key, fallback) {
  if (!t) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export function getRiskBadgeTone(level) {
  return RISK_LEVEL_CLASS[normalizeLevel(level)] || RISK_LEVEL_CLASS.unknown;
}

export function getRiskFlagLabel(flag, { t = null } = {}) {
  const key = normalizeRiskFlag(flag);
  if (t) {
    return resolveCatalogLabel(
      t,
      `automotive.detail.risk.flags.${key}.label`,
      RISK_FLAG_LABELS_ES[key] || key
    );
  }
  return RISK_FLAG_LABELS_ES[key] || flag;
}

export function getRiskFlagDescription(flag, { t = null } = {}) {
  const key = normalizeRiskFlag(flag);
  if (t) {
    return resolveCatalogLabel(
      t,
      `automotive.detail.risk.flags.${key}.description`,
      RISK_FLAG_DESCRIPTIONS_ES[key] || ""
    );
  }
  return RISK_FLAG_DESCRIPTIONS_ES[key] || "";
}

export function getBuyerQuestionsForRiskFlags(flags = [], { limit = 6, t = null } = {}) {
  const questions = [];
  const seen = new Set();
  flags.forEach((flag) => {
    const key = normalizeRiskFlag(flag);
    const question = t
      ? resolveCatalogLabel(
          t,
          `automotive.detail.risk.questions.${key}`,
          RISK_FLAG_QUESTIONS_ES[key]
        )
      : RISK_FLAG_QUESTIONS_ES[key];
    if (question && !seen.has(question)) {
      seen.add(question);
      questions.push({ flag: key, question });
    }
  });

  const defaultKeys = ["available", "mileage", "soat", "inspection"];
  defaultKeys.forEach((defaultKey) => {
    if (questions.length >= limit) return;
    const question = t
      ? resolveCatalogLabel(
          t,
          `automotive.detail.risk.defaultQuestions.${defaultKey}`,
          null
        )
      : null;
    const fallback = {
      available: "¿El vehículo sigue disponible?",
      mileage: "¿Cuál es el kilometraje actual?",
      soat: "¿Tiene SOAT y tecnomecánica al día?",
      inspection: "¿Puedo hacer un peritaje antes de comprar?",
    }[defaultKey];
    const resolved = question || fallback;
    if (resolved && !seen.has(resolved)) {
      seen.add(resolved);
      questions.push({ flag: null, question: resolved });
    }
  });

  return questions.slice(0, limit);
}

export function shouldShowRiskBadge(listing, variant = "default") {
  const level = resolveRiskLevel(listing);
  if (variant === "search_card" || variant === "card") {
    return level !== RISK_LEVELS.LOW && level !== RISK_LEVELS.UNKNOWN;
  }
  if (variant === "recommendation") {
    return [RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(level);
  }
  if (variant === "dealer_inventory") {
    return [RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH, RISK_LEVELS.UNKNOWN].includes(level);
  }
  return level !== RISK_LEVELS.LOW;
}

export function shouldShowRiskPanel(listing) {
  return Boolean(listing);
}

export function getRiskTooltip(listing, { t = null } = {}) {
  const level = resolveRiskLevel(listing);
  if (t) {
    return resolveCatalogLabel(
      t,
      `automotive.badges.riskTooltip.${level}`,
      RISK_LEVEL_TOOLTIPS_ES[level] || RISK_LEVEL_TOOLTIPS_ES.unknown
    );
  }
  return RISK_LEVEL_TOOLTIPS_ES[level] || RISK_LEVEL_TOOLTIPS_ES.unknown;
}

export function getBuyerVisibleIndicators(listing) {
  const vm = getVehicleRiskViewModel(listing);
  if (listing?.buyerVisibleRiskIndicators?.length) {
    return listing.buyerVisibleRiskIndicators;
  }
  return vm.visibleFlags.map((flag) => ({
    type: flag,
    message: getRiskFlagLabel(flag),
    description: getRiskFlagDescription(flag),
    question: RISK_FLAG_QUESTIONS_ES[flag],
  }));
}

export function getVehicleRiskViewModel(listing, { t = null } = {}) {
  const flags = deriveRiskFlagsFromListing(listing);
  const level = resolveRiskLevel(listing);
  const visibleFlags = flags.filter((f) => f !== RISK_FLAGS.NO_RISK_DATA).slice(0, 8);
  const hasHighPriorityFlags = visibleFlags.some((f) => HIGH_PRIORITY_FLAGS.has(f));
  const buyerQuestions = getBuyerQuestionsForRiskFlags(visibleFlags, { t });

  return {
    level,
    displayLabel: getRiskLevelLabel(level, { t }),
    shortLabel: getRiskLevelLabel(level, { short: true, t }),
    description: t
      ? resolveCatalogLabel(
          t,
          `automotive.detail.risk.descriptions.${level}`,
          RISK_DETAIL_COPY_ES[level] || RISK_DETAIL_COPY_ES.unknown
        )
      : RISK_DETAIL_COPY_ES[level] || RISK_DETAIL_COPY_ES.unknown,
    detailTitle: t
      ? resolveCatalogLabel(
          t,
          `automotive.detail.risk.titles.${level}`,
          RISK_DETAIL_TITLES_ES[level] || RISK_DETAIL_TITLES_ES.unknown
        )
      : RISK_DETAIL_TITLES_ES[level] || RISK_DETAIL_TITLES_ES.unknown,
    badgeTone: getRiskBadgeTone(level),
    tooltip: getRiskTooltip(listing, { t }),
    flags,
    visibleFlags,
    buyerQuestions,
    hasRiskData: level !== RISK_LEVELS.UNKNOWN || visibleFlags.length > 0,
    hasHighPriorityFlags,
    buyerNote: RISK_DISCLAIMER_ES,
    showBadge: level !== RISK_LEVELS.LOW,
    showBadgeOnCard: level !== RISK_LEVELS.LOW && level !== RISK_LEVELS.UNKNOWN,
  };
}

// Backward-compatible helpers
export function normalizeRiskLevelForCss(level) {
  return String(level || "unknown").toLowerCase().replace(/\s+/g, "-");
}

export function getRiskDisclaimer() {
  return RISK_DISCLAIMER_ES;
}

export function getRecommendationRiskBoost(subject) {
  const level = resolveRiskLevel(subject);
  return RECOMMENDATION_RISK_ADJUSTMENTS[level] ?? 0;
}

export function isUnavailableForRecommendation(subject) {
  return resolveRiskLevel(subject) === RISK_LEVELS.UNAVAILABLE;
}

export function getDealerFixSuggestion(indicatorType) {
  const map = {
    stale_listing: "Confirma disponibilidad",
    missing_photos: "Agrega fotos",
    missing_price: "Agrega precio",
    missing_mileage: "Agrega kilometraje",
  };
  return map[indicatorType] || "Revisar publicación";
}
